import React, { useState, useEffect } from 'react';
import { ArrowRight, AlertTriangle, Cpu, TrendingUp } from 'lucide-react';

export default function CoachBentoDashboard({ clients, onFilterChange, onSelectAthlete }) {
  const [typedText, setTypedText] = useState('');
  
  const totalAthletes = clients.length;
  const validClients = clients.filter(c => c.recentStatus !== 'Sin datos');
  const alertaAthletes = validClients.filter(c => c.recentStatus === 'Fatiga Alta' || c.readinessScore < 40);
  const cargaAthletes = validClients.filter(c => c.recentStatus === 'Recuperación' || (c.readinessScore >= 40 && c.readinessScore < 70));
  const listosAthletes = validClients.filter(c => c.recentStatus === 'Óptimo' || c.readinessScore >= 70);

  // Derive top 4 athletes for the panel (prioritize alerts)
  const topAthletes = [...clients].sort((a, b) => {
    // Si no tienen datos, los mandamos al final
    if (a.recentStatus === 'Sin datos' && b.recentStatus !== 'Sin datos') return 1;
    if (a.recentStatus !== 'Sin datos' && b.recentStatus === 'Sin datos') return -1;
    const scoreA = a.readinessScore || 100;
    const scoreB = b.readinessScore || 100;
    return scoreA - scoreB; // lower readiness first
  }).slice(0, 4);

  const getStatusInfo = (athlete) => {
    if (athlete.recentStatus === 'Sin datos') return { text: 'sin datos', class: '', tsb: '--' };
    const score = athlete.readinessScore || 100;
    const realTsb = athlete.tsb !== undefined && athlete.tsb !== '--' ? Math.round(athlete.tsb) : '--';
    if (score < 40 || athlete.recentStatus === 'Fatiga Alta') return { text: 'alerta', class: 'badge-alerta', tsb: realTsb };
    if (score < 70 || athlete.recentStatus === 'Recuperación') return { text: 'carga', class: 'badge-carga', tsb: realTsb };
    return { text: 'listo', class: 'badge-listo', tsb: realTsb };
  };

  const donutPercentage = validClients.length === 0 ? 0 : (alertaAthletes.length / validClients.length) * 100;
  
  // Terminal Text Logic
  const fullTerminalText = `> Equipo: ${totalAthletes} atletas activos
> En alerta: ${alertaAthletes.length} (${donutPercentage.toFixed(1)}%)
> Listos para competir: ${listosAthletes.length}

Recomendación: ${alertaAthletes.length > 0 ? `Reduce carga general un 15% esta semana. Presta atención inmediata a ${alertaAthletes.map(a => a.name).join(', ')}.` : 'El equipo se encuentra en fase de asimilación óptima. Mantén la periodización actual.'}`;

  useEffect(() => {
    let currentText = '';
    let i = 0;
    const interval = setInterval(() => {
      currentText += fullTerminalText.charAt(i);
      setTypedText(currentText);
      i++;
      if (i >= fullTerminalText.length) clearInterval(interval);
    }, 20); // Typing speed
    return () => clearInterval(interval);
  }, [totalAthletes, alertaAthletes.length]);

  return (
    <div className="coach-bento-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '1.5rem' }}>
      
      {/* CARD 1: Alertas Predictivas (Left/Top) */}
      <div className="coach-bento-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '1.5rem' }}>
          Alertas Predictivas
        </p>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          {/* Donut Chart SVG */}
          <div style={{ position: 'relative', width: '100px', height: '100px', flexShrink: 0 }}>
            <svg width="100" height="100" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
              <circle 
                cx="50" cy="50" r="40" fill="none" 
                stroke="#ef4444" strokeWidth="8" 
                strokeDasharray="251.2" 
                strokeDashoffset={251.2 - (251.2 * donutPercentage) / 100}
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
                style={{ transition: 'stroke-dashoffset 1s ease-out' }}
              />
            </svg>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '1.8rem', fontWeight: 800, color: alertaAthletes.length > 0 ? '#ef4444' : '#10b981', lineHeight: 1 }}>{alertaAthletes.length}</span>
              <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>alertas</span>
            </div>
          </div>
          
          <div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.5rem', color: '#fff' }}>
              {alertaAthletes.length > 0 ? 'Riesgo detectado.' : 'Sistema nominal.'}
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1rem', lineHeight: 1.5, marginBottom: '1rem' }}>
              {alertaAthletes.length > 0 
                ? `${alertaAthletes.length} atletas superan el umbral de fatiga acumulada. Intervención recomendada.` 
                : 'Ningún atleta supera el umbral crítico de fatiga acumulada.'}
            </p>
            {alertaAthletes.length > 0 && (
              <button style={{ 
                background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', 
                padding: '0.4rem 1rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.5rem'
              }}>
                <AlertTriangle size={14} /> Enviar pauta de descanso
              </button>
            )}
          </div>
        </div>
      </div>

      {/* CARD 2: Informe IA (Right/Bottom) */}
      <div className="coach-bento-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>
            Informe IA · Esta Semana
          </p>
          <Cpu size={18} color="#8b5cf6" />
        </div>
        
        <div className="terminal-box">
          <div className="typewriter-text">{typedText}</div>
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
          {['Multi-atleta', 'IA Generativa', 'Alertas', 'B2B'].map(tag => (
            <span key={tag} style={{ 
              background: 'rgba(139, 92, 246, 0.05)', border: '1px solid rgba(139, 92, 246, 0.15)',
              color: '#a78bfa', fontSize: '0.75rem', padding: '0.3rem 0.8rem', borderRadius: '12px', fontWeight: 600
            }}>
              {tag}
            </span>
          ))}
        </div>
      </div>

    </div>
  );
}
